import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { File02, Link01, Upload01, FileCheck02, Grid01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { useTranslation } from 'react-i18next';

export const DocumentsAdminPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active tab from route
    const getActiveTab = () => {
        if (location.pathname.includes('/connections')) return 'connections';
        if (location.pathname.includes('/requirements')) return 'requirements';
        if (location.pathname.includes('/training')) return 'training';
        if (location.pathname.includes('/attributes')) return 'attributes';
        return 'types'; // default (includes /types and all sub-routes)
    };

    const [activeTab, setActiveTab] = useState(getActiveTab());

    // Update active tab when route changes
    useEffect(() => {
        setActiveTab(getActiveTab());
    }, [location.pathname]);

    const handleTabChange = (key: string) => {
        setActiveTab(key as string);
        const basePath = '/admin/documents';
        switch (key) {
            case 'types':
                navigate(`${basePath}/types`);
                break;
            case 'attributes':
                navigate(`${basePath}/attributes`);
                break;
            case 'connections':
                navigate(`${basePath}/connections`);
                break;
            case 'requirements':
                navigate(`${basePath}/requirements`);
                break;
            case 'training':
                navigate(`${basePath}/training`);
                break;
            default:
                navigate(`${basePath}/types`);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-primary">{t('title')}</h1>
                <p className="text-tertiary mt-1">{t('subtitle')}</p>
            </div>

            {/* View Switcher - Stile Container */}
            <div className="flex items-center gap-2 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt">
                <Button
                    size="sm"
                    color={activeTab === 'types' ? 'primary' : 'secondary'}
                    onClick={() => handleTabChange('types')}
                    iconLeading={File02}
                >
                    {t('tabs.types')}
                </Button>
                <Button
                    size="sm"
                    color={activeTab === 'attributes' ? 'primary' : 'secondary'}
                    onClick={() => handleTabChange('attributes')}
                    iconLeading={FileCheck02}
                >
                    {t('tabs.attributes')}
                </Button>
                <Button
                    size="sm"
                    color={activeTab === 'connections' ? 'primary' : 'secondary'}
                    onClick={() => handleTabChange('connections')}
                    iconLeading={Link01}
                >
                    {t('tabs.connections')}
                </Button>
                <Button
                    size="sm"
                    color={activeTab === 'requirements' ? 'primary' : 'secondary'}
                    onClick={() => handleTabChange('requirements')}
                    iconLeading={Grid01}
                >
                    {t('tabs.requirements')}
                </Button>
                <Button
                    size="sm"
                    color={activeTab === 'training' ? 'primary' : 'secondary'}
                    onClick={() => handleTabChange('training')}
                    iconLeading={Upload01}
                >
                    {t('tabs.training')}
                </Button>
            </div>

            {/* Content with spacing */}
            <div className="mt-6">
                <Outlet />
            </div>
        </div>
    );
};
